import type { PriceUnitDto } from '@/api';
import { BaseModal } from '@/components/base-modal';
import Combobox, {
  type DefaultOptionType,
} from '@/components/combobox/combobox';
import { useFilteredPriceUnits } from '@/components/combobox/combobox-queries';
import { LabeledField } from '@/components/form/labeled-field';
import NumericTextField from '@/components/form/numeric-text-field';
import { HelpText } from '@lego/connect-components-react';
import { Flex } from '@lego/klik-ui';
import type { RefObject } from 'react';
import React from 'react';

type CreateCalculationSettingsModalProps = {
  isOpen: boolean;
  triggerRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onCreate: (priceUnit: PriceUnitDto, year: number) => void;
  existingPriceUnitYearKeys: Set<string>;
};

export const CreateCalculationSettingsModal = ({
  isOpen,
  triggerRef,
  onClose,
  onCreate,
  existingPriceUnitYearKeys,
}: CreateCalculationSettingsModalProps) => {
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedItem, setSelectedItem] = React.useState<
    DefaultOptionType | undefined
  >();
  const [selectedPriceUnit, setSelectedPriceUnit] = React.useState<
    PriceUnitDto | undefined
  >();
  const [year, setYear] = React.useState(new Date().getFullYear() + 1);

  const { data: searchResults, isFetching } =
    useFilteredPriceUnits(searchValue);

  const filteredOptions: DefaultOptionType[] = React.useMemo(
    () =>
      (searchResults ?? []).map((pu) => ({
        id: String(pu.id),
        label: `${pu.id} - ${pu.name ?? 'N/A'}`,
      })),
    [searchResults],
  );

  const handleClose = () => {
    setSearchValue('');
    setSelectedItem(undefined);
    setSelectedPriceUnit(undefined);
    setYear(new Date().getFullYear() + 1);
    onClose();
  };

  const currentYear = new Date().getFullYear();
  const minYear = currentYear;
  const maxYear = currentYear + 2;

  const getYearError = (): string | undefined => {
    if (!year) return 'Year is required';
    if (year < minYear || year > maxYear)
      return `Year must be between ${minYear} and ${maxYear}`;
    return undefined;
  };

  const yearError = getYearError();
  const isDuplicate = selectedPriceUnit
    ? existingPriceUnitYearKeys.has(`${selectedPriceUnit.id}-${year}`)
    : false;
  const isValid = !!selectedPriceUnit && !yearError && !isDuplicate;

  const handleCreate = () => {
    if (!selectedPriceUnit || !isValid) return;
    onCreate(selectedPriceUnit, Number(year));
    handleClose();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      triggerRef={triggerRef}
      onClose={handleClose}
      title="Add Calculation Settings"
      rightButtonProps={{
        text: 'Create',
        onClick: handleCreate,
        disabled: !isValid,
      }}
    >
      <Flex flexDirection="column" gap={4} width="100%">
        <LabeledField label="Year">
          <NumericTextField
            integerOnly
            value={String(year)}
            validationState={yearError ? 'invalid' : 'valid'}
            required
            onChange={(_e, value) => {
              if (value.length <= 4) {
                setYear(Number(value));
                setSelectedItem(undefined);
                setSelectedPriceUnit(undefined);
              }
            }}
          />
          {yearError && <HelpText variant="negative">{yearError}</HelpText>}
        </LabeledField>
        <div>
          <LabeledField label="Price Unit">
            <Combobox
              options={filteredOptions}
              selectedItems={selectedItem ? [selectedItem] : []}
              isLoading={isFetching}
              inputValue={searchValue}
              onInputChange={setSearchValue}
              placeholder="Type number or description"
              isRequired={true}
              onSelect={(items) => {
                const item = items[items.length - 1];
                if (item) {
                  setSelectedItem(item);
                  setSelectedPriceUnit(
                    searchResults?.find((pu) => String(pu.id) === item.id),
                  );
                  setSearchValue('');
                } else {
                  setSelectedItem(undefined);
                  setSelectedPriceUnit(undefined);
                }
              }}
            />
          </LabeledField>
          {isDuplicate && (
            <HelpText variant="negative">
              Settings already exist for this price unit and year
            </HelpText>
          )}
        </div>
      </Flex>
    </BaseModal>
  );
};
